<script setup>
import {HpoRole, HpoTransportDestinationRequestStatus, HpoTransportStatus} from "../../../choices";
import {useMutation} from "@vue/apollo-composable";
import {HpoSetPatientInHospitalMutation} from "@/graphql/mutations/hpo/patient/setPatientInHospital.graphql"
import {HpoPatientTransportStartMutation} from "@/graphql/mutations/hpo/patient/transportStart.graphql"
import {computed} from "vue";
import {mdiCancel, mdiCheckCircleOutline, mdiCloseCircleOutline, mdiHospitalBoxOutline, mdiTruckFast} from "@mdi/js";
import {getRemainingTimeMessage} from "./utils";

const props = defineProps({
  patient: {
    type: Object,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  now: {
    type: Object,
    required: true
  },
})
const emit = defineEmits(['setEditedTransportData', 'openReplyModal'])

const {mutate: setPatientInHospitalMutation} = useMutation(HpoSetPatientInHospitalMutation)
const {mutate: transportStartMutation, loading: transportStartLoading} = useMutation(HpoPatientTransportStartMutation)


const isAlert = computed(() => {
  return showAssignHospitalButton.value || showContactPointPendingButtons.value
})

const isProperMemberType = computed(() => {
  return [HpoRole.ZOS, HpoRole.CONTACT_POINT].includes(props.member.role)
})

const isEmsManager = computed(() => {
  return [HpoRole.ZOS, HpoRole.EVAC_MANAGER].includes(props.member.role)
})

const showAssignHospitalButton = computed(() => {
  return !!(
    (props.member.role === HpoRole.ZOS) &&
    (props.patient.transportStatus === HpoTransportStatus.DISP_PENDING)
  )
})

const showContactPointPendingButtons = computed(() => {
  return isProperMemberType.value && props.patient.transportStatus === HpoTransportStatus.HOSPITAL_PENDING
})

const showTransportStartButton = computed(() => {
  return isEmsManager.value && props.patient.transportStatus === HpoTransportStatus.READY
});

const showContactPointOnWayButtons = computed(() => {
  return isProperMemberType.value && props.patient.transportStatus === HpoTransportStatus.ON_WAY
})


function setEditedTransportData() {
  emit('setEditedTransportData', props.patient)
}

function openReplyModal(reply) {
  emit('openReplyModal', reply, props.patient)
}

function setPatientInHospital() {
  setPatientInHospitalMutation({
    patientId: props.patient.id,
    hospitalId: props.patient.hospital.id
  })
}

function transportStart() {
  transportStartMutation({
    patientId: props.patient.id,
  })
}
</script>


<template>
  <div class="d-flex flex-column">
    <!-- ASSIGN HOSPITAL BUTTON -->
    <v-btn v-if="showAssignHospitalButton"
           density="compact"
           :prepend-icon="mdiHospitalBoxOutline"
           :text="pgettext('action', 'choose hospital')"
           @click="setEditedTransportData"
    ></v-btn>

    <template v-else-if="patient.hospital">
      <div><strong>{{ patient.hospital.name }}</strong></div>
      <div v-if="patient.destinationWard">{{ patient.destinationWard }}</div>
      <div>{{ getRemainingTimeMessage(patient.eta, patient.transportMinutes, now) }}</div>

      <!-- ON WAY HOSPITAL-PENDING BUTTONS -->
      <template v-if="showContactPointPendingButtons">
        <v-btn density="compact"
               color="green"
               class="mt-1"
               :text="pgettext('action', 'accept')"
               :prepend-icon="mdiCheckCircleOutline"
               @click="openReplyModal(HpoTransportDestinationRequestStatus.ACCEPTED)"
        ></v-btn>

        <v-btn density="compact"
               color="red"
               :text="pgettext('action', 'reject')"
               :prepend-icon="mdiCloseCircleOutline"
               @click="openReplyModal(HpoTransportDestinationRequestStatus.REFUSED)"
        ></v-btn>
      </template>

      <!-- TRANSPORT START BUTTON -->
      <v-btn v-else-if="showTransportStartButton"
             density="compact"
             class="mt-1"
             :prepend-icon="mdiTruckFast"
             :disabled="transportStartLoading"
             :loading="transportStartLoading"
             @click="transportStart()">
        {{ pgettext('action', 'start transport') }}
      </v-btn>

      <!-- ON WAY BUTTONS -->
      <template v-else-if="showContactPointOnWayButtons">
        <v-btn density="compact"
               color="red"
               class="mt-1"
               :text="pgettext('action', 'cancel')"
               :prepend-icon="mdiCancel"
               @click="openReplyModal(HpoTransportDestinationRequestStatus.CANCELED)"
        ></v-btn>

        <v-btn class="pa-1 h-auto"
               @click="setPatientInHospital()">
          <span class="text-wrap">{{ pgettext('label', 'Patient in hospital') }}</span>
        </v-btn>
      </template>
    </template>
  </div>
</template>

